import type { ChartType } from '../../models/chart.types'
import type { ReportRow } from '../../models/report.types'

import type {
  BuildCategoricalChartOptionsInput,
  CategoricalChartOptions,
} from './chartBuilder.types'
import { finiteNumber } from './chartData'
import {
  buildChartMetricAxes,
  buildChartTooltip,
  chartFieldLabel,
  formatChartValue,
} from './chartPresentation'
import {
  buildChartTimeTicks,
  expandChartBreakdownRows,
  formatChartTimeLabel,
  isNumericChartTimeField,
  minimumChartTimeLabelWidth,
  normalizeChartTimeValue,
  sampleChartTimeTicks,
} from './chartTime'
import { firstField, manyFields, type ResolvedChartFields } from './resolveChartFields'

type TimeSeriesType = Extract<ChartType, 'line' | 'area' | 'stacked_area' | 'multi_line'>

interface TimePoint {
  x: number
  y: number
  custom: { metric: string; row: ReportRow }
}

interface TimeSeries {
  type: 'spline' | 'area'
  name: string
  yAxis: number
  data: TimePoint[]
  custom: { metric: string }
}

function combinedValue(row: ReportRow, fields: readonly string[]): string {
  return fields.map((field) => String(row[field] ?? '-')).join(' / ')
}

function timeFields(
  type: TimeSeriesType,
  fields: ResolvedChartFields,
): { time: string; groups: string[]; metrics: string[] } | null {
  if (type === 'multi_line') {
    const time = firstField(fields, ['time_dimension'])
    const group = firstField(fields, ['dimension'])
    const metric = firstField(fields, ['metric'])
    return time && group && metric ? { time, groups: [group], metrics: [metric] } : null
  }
  const time = firstField(fields, ['time_dimension', 'dimension', 'dimensions'])
  const dimensions = manyFields(fields, ['dimensions'])
  const metrics = manyFields(fields, ['metrics'])
  return time && metrics.length
    ? { time, groups: dimensions.filter((field) => field !== time), metrics }
    : null
}

/** Renderer default for callers without resolved catalog presentation. */
const DEFAULT_SERIES_GROUP_LIMIT = 5

function groupedRows(
  rows: readonly ReportRow[],
  groupFields: readonly string[],
  limit: number,
): Array<[string, ReportRow[]]> {
  if (groupFields.length === 0) return [['', [...rows]]]
  const groups = new Map<string, ReportRow[]>()
  for (const row of rows) {
    const key = combinedValue(row, groupFields)
    const current = groups.get(key) ?? []
    current.push(row)
    groups.set(key, current)
  }
  return [...groups.entries()].slice(0, limit)
}

function points(rows: readonly ReportRow[], time: string, metric: string): TimePoint[] {
  const byTime = new Map<number, TimePoint>()
  for (const row of rows) {
    const x = normalizeChartTimeValue(row[time], time, row)
    if (x === null) continue
    byTime.set(x, {
      x,
      y: finiteNumber(row[metric]),
      custom: { metric, row },
    })
  }
  return [...byTime.values()].sort((left, right) => left.x - right.x)
}

function fillStackedTimeline(series: readonly TimeSeries[]): TimeSeries[] {
  const timeline = [...new Set(series.flatMap((item) => item.data.map((point) => point.x)))].sort(
    (left, right) => left - right,
  )
  return series.map((item) => {
    const pointsByTime = new Map(item.data.map((point) => [point.x, point]))
    return {
      ...item,
      data: timeline.map(
        (x) =>
          pointsByTime.get(x) ?? {
            x,
            y: 0,
            custom: { metric: item.custom.metric, row: {} },
          },
      ),
    }
  })
}

function seriesName(
  input: BuildCategoricalChartOptionsInput,
  group: string,
  metric: string,
  metricCount: number,
): string {
  if (!group) return chartFieldLabel(input, metric)
  return metricCount === 1 ? group : `${chartFieldLabel(input, metric)} · ${group}`
}

export function buildTimeSeriesChartOptions(
  input: BuildCategoricalChartOptionsInput,
  type: TimeSeriesType,
  fields: ResolvedChartFields,
): CategoricalChartOptions | null {
  const resolved = timeFields(type, fields)
  if (!resolved) return null
  const rows = expandChartBreakdownRows(input.rows)
  const groups = groupedRows(
    rows,
    resolved.groups,
    input.presentation?.maxSeries ?? DEFAULT_SERIES_GROUP_LIMIT,
  )
  const { yAxis, metricAxisIndex } = buildChartMetricAxes(input, resolved.metrics)
  const seriesType = type === 'area' || type === 'stacked_area' ? 'area' : 'spline'
  let series: TimeSeries[] = groups.flatMap(([group, groupRows]) =>
    resolved.metrics.map((metric) => ({
      type: seriesType,
      name: seriesName(input, group, metric, resolved.metrics.length),
      yAxis: metricAxisIndex.get(metric) ?? 0,
      data: points(groupRows, resolved.time, metric),
      custom: { metric },
    })),
  )
  series = series.filter((item) => item.data.length > 0)
  if (series.length === 0) return null
  if (type === 'stacked_area') series = fillStackedTimeline(series)

  const ticks = [...new Set(series.flatMap((item) => item.data.map((point) => point.x)))].sort(
    (left, right) => left - right,
  )
  const time = resolved.time
  const title = resolved.groups.map((field) => chartFieldLabel(input, field)).join(' / ')

  return {
    chart: {
      type: seriesType,
      zoomType: 'x',
      backgroundColor: 'transparent',
    },
    title: { text: title, style: { fontSize: '12px', fontWeight: '700' } },
    xAxis: {
      type: isNumericChartTimeField(time) ? 'linear' : 'datetime',
      ordinal: false,
      crosshair: true,
      startOnTick: false,
      endOnTick: false,
      min: ticks[0],
      max: ticks.at(-1),
      tickPositioner(this: { min?: number; max?: number; chart?: { plotWidth?: number } }) {
        const minimum = this.min ?? ticks[0]!
        const maximum = this.max ?? ticks.at(-1)!
        const allTicks = buildChartTimeTicks(minimum, maximum, time)
        const availableWidth = this.chart?.plotWidth ?? 1_000
        return sampleChartTimeTicks(
          allTicks,
          Math.max(1, Math.floor(availableWidth / minimumChartTimeLabelWidth(time))),
        )
      },
      labels: {
        autoRotation: [-35, -50, -90],
        allowOverlap: false,
        style: { fontSize: '11px' },
        formatter(this: { value: number }) {
          return formatChartTimeLabel(Number(this.value), time)
        },
      },
    },
    yAxis,
    legend: {
      itemStyle: {
        color: 'var(--ui-chart-legend)',
        fontSize: '12px',
        fontWeight: '400',
      },
      useHTML: true,
    },
    tooltip: {
      shared: true,
      outside: true,
      useHTML: true,
      formatter() {
        const context = this as unknown as {
          x: number
          points?: Array<{
            color?: unknown
            y?: unknown
            series: { name: string; userOptions?: { custom?: { metric?: string } } }
          }>
        }
        return buildChartTooltip(
          formatChartTimeLabel(context.x, time),
          (context.points ?? []).map((point) => {
            const metric = point.series.userOptions?.custom?.metric ?? resolved.metrics[0]!
            return {
              color: point.color,
              label: point.series.name,
              value: formatChartValue(input, point.y, metric),
            }
          }),
        )
      },
    },
    responsive: {
      rules: [
        {
          condition: { maxWidth: 420 },
          chartOptions: { xAxis: { labels: { rotation: -35, style: { fontSize: '10px' } } } },
        },
        {
          condition: { maxWidth: 260 },
          chartOptions: { xAxis: { labels: { rotation: -60, style: { fontSize: '9px' } } } },
        },
      ],
    },
    plotOptions: {
      series: { lineWidth: 2 },
      ...(seriesType === 'area'
        ? {
            area: {
              stacking: type === 'stacked_area' ? 'normal' : undefined,
              fillOpacity: 0.2,
              marker: { enabled: true, radius: 3 },
            },
          }
        : {}),
    },
    series,
    credits: { enabled: false },
  } as CategoricalChartOptions
}
